
  import { computed, defineComponent, onMounted, ref } from 'vue';
  import { ErrorMessage, Field, Form } from 'vee-validate';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { useRouter } from 'vue-router';
  import { Actions } from '@/store/enums/StoreEnums';
  import { IndexTypeViewTypeObject } from '@/store/modules/Indices/IndicesAssetsModule';
  import { IndicesType } from '@/store/enums/TypeOfIndicesEnums';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import * as Yup from 'yup';

  export default defineComponent({
    name: 'date-range-setup-creating',
    components: {
      ErrorMessage,
      Field,
      Form,
    },
    async setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const submitButton = ref<HTMLButtonElement | null>(null);
      const { can } = useAbility();

      const translate = (text) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      const goBack = () => {
        router.back();
      };

      onMounted(() => {
        setCurrentPageBreadcrumbs(translate('priceIndexDateRangeCreating'), [
          translate('indices'),
        ]);
      });

      const currentYear = new Date().getFullYear();
      const MIN_YEAR = 2000;
      const yearOptions: number[] = Array.from(
        { length: currentYear - MIN_YEAR + 1 },
        (_, i) => MIN_YEAR + i
      );

      const intervals = ref<{ id: number; key: string }[]>([]);
      const indexTypes = ref<
        { id: number; slug: string; name_ar: string; name_en: string }[]
      >([]);
      const isDefault = ref<boolean>(false);
      const selectedTypeId = ref<number | ''>('');
      const checkboxKey = (intervalId: number, viewTypeId: number) =>
        `${intervalId}_${viewTypeId}`;
      const checkedIntervalViewTypes = ref<Record<string, boolean>>({});

      const [intervalsRes, indexTypesRes, priceVTs, volumeVTs] =
        await Promise.all([
          store.dispatch(Actions.GET_INDEX_INTERVALS),
          store.dispatch(Actions.GET_INDEX_TYPES),
          store.dispatch(
            Actions.GET_ALL_INDEX_TYPE_VIEW_TYPES,
            IndicesType.PRICE_INDEX.id
          ),
          store.dispatch(
            Actions.GET_ALL_INDEX_TYPE_VIEW_TYPES,
            IndicesType.VOLUME_INDEX.id
          ),
        ]);
      intervals.value = intervalsRes?.data?.data ?? intervalsRes?.data ?? [];
      indexTypes.value = indexTypesRes?.data?.data ?? indexTypesRes?.data ?? [];

      const viewTypesByIndexType = ref<
        Record<number, IndexTypeViewTypeObject[]>
      >({
        [IndicesType.PRICE_INDEX.id]: priceVTs ?? [],
        [IndicesType.VOLUME_INDEX.id]: volumeVTs ?? [],
      });

      const currentViewTypes = computed(
        () => viewTypesByIndexType.value[Number(selectedTypeId.value)] ?? []
      );

      const isChecked = (intervalId: number, viewTypeId: number) =>
        !!checkedIntervalViewTypes.value[checkboxKey(intervalId, viewTypeId)];

      const onTypeIdChange = () => {
        checkedIntervalViewTypes.value = {};
      };

      const onIntervalViewTypeToggle = (
        intervalId: number,
        viewTypeId: number,
        checked: boolean
      ) => {
        checkedIntervalViewTypes.value = {
          ...checkedIntervalViewTypes.value,
          [checkboxKey(intervalId, viewTypeId)]: checked,
        };
      };

      const validationSchema = Yup.object().shape({
        typeId: Yup.number()
          .required(() => translate('TYPE_ID_IS_REQUIRED_FIELD'))
          .label('Index Type'),
        titleAr: Yup.string()
          .required(() => translate('TITLE_AR_IS_REQUIRED_FIELD'))
          .label('Title Ar'),
        titleEn: Yup.string()
          .required(() => translate('TITLE_EN_IS_REQUIRED_FIELD'))
          .label('Title En'),
        startYear: Yup.number()
          .required(() => translate('START_YEAR_IS_REQUIRED_FIELD'))
          .integer()
          .min(1900)
          .label('Start Year'),
        endYear: Yup.number()
          .required(() => translate('END_YEAR_IS_REQUIRED_FIELD'))
          .integer()
          .min(1900)
          .label('End Year'),
        sort: Yup.number()
          .required(() => translate('SORT_IS_REQUIRED_FIELD'))
          .integer()
          .min(0)
          .label('Sort'),
      });

      const onSubmitCreate = async (values) => {
        const supportedIntervalsPayload = Object.entries(
          checkedIntervalViewTypes.value
        )
          .filter(([, checked]) => checked)
          .map(([key]) => {
            const [intervalId, viewTypeId] = key.split('_').map(Number);
            return { intervalId, viewTypeId };
          });

        if (!supportedIntervalsPayload.length) {
          Swal.fire({
            text: translate('SUPPORTED_INTERVALS_IS_REQUIRED_FIELD'),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: { confirmButton: 'btn fw-bold btn-light-danger' },
          });
          return;
        }

        if (submitButton.value) {
          submitButton.value!.disabled = true;
          submitButton.value.setAttribute('data-kt-indicator', 'on');
        }

        const payload = {
          typeId: Number(values.typeId),
          titleAr: values.titleAr,
          titleEn: values.titleEn,
          startYear: Number(values.startYear),
          endYear: Number(values.endYear),
          sort: Number(values.sort),
          supportedIntervals: supportedIntervalsPayload,
          isDefault: isDefault.value,
        };

        await store.dispatch(Actions.CREATE_PRICE_INDEX_DATE_RANGE, payload);
        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];

        if (error) {
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          submitButton.value?.removeAttribute('data-kt-indicator');
          submitButton.value!.disabled = false;
        } else {
          Swal.fire({
            text: translate('SUCCESSFULLY_CREATED_INDEX_DATE_RANGE'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          }).then(() => {
            router.push({ name: 'date-range-setup-listing' });
          });
        }
      };

      store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');

      return {
        onSubmitCreate,
        submitButton,
        translate,
        validationSchema,
        goBack,
        can,
        intervals,
        indexTypes,
        currentViewTypes,
        isChecked,
        selectedTypeId,
        onTypeIdChange,
        onIntervalViewTypeToggle,
        isDefault,
        currentYear,
        yearOptions,
      };
    },
  });
